import {
  isUserCompanyManager,
  isUserStudent,
  isUserTeacher,
  isUserTeamManager,
  useSignout,
} from "../../../services/authUtil";
import { ReactComponent as ExpandMoreIcon } from "../../../images/expand_more.svg";
import { ReactComponent as PersonalIcon } from "../../../images/personal_pic.svg";
import { ReactComponent as LoginIcon } from "../../../images/login_icon.svg";
import { ReactComponent as MoreHorizIcon } from "../../../images/more_horiz.svg";
import { ReactComponent as CaretRightIcon } from "images/caret_right.svg";
import UserNotifications from "../../UserNotifications/UserNotifications";
import translateIcon from "../../../images/translation_nav_icon.svg";
import { getUserFullName } from "../../../services/util";
import { FC, MouseEvent, useEffect, useRef, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Popover } from "@material-ui/core";
import NavPanel from "../../NavPanel";
import { useTabs } from "../../../hooks/useTabs";
import Logo from "../../Logo";
import "./PageHeader.css";
import classnames from "classnames";
import { useTranslation } from "react-i18next";
import LanguageSelector, {
  LanguageDropdown,
  LANGUAGES,
} from "components/LanguageSelector";
import { AdaptiveTestStatus } from "model/adaptive_test";
import useUser from "hooks/useUser";

interface IPageHeader {
  smallWindow: any;
  toggleSidebar: any;
}

const PageHeader: FC<IPageHeader> = ({ smallWindow, toggleSidebar }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { t, i18n } = useTranslation();
  const [languageAnchorEl, setLanguageAnchorEl] = useState<null | HTMLElement>(null);
  const moreIconRef = useRef<null | HTMLButtonElement>(null);
  const location = window.location.pathname;
  const [lang, setLang] = useState(LANGUAGES[0].value);
  const user = useUser();
  const signOut = useSignout();

  const handleSignOut = () => {
    handleClose();
    signOut();
  };

  useEffect(() => {
    const uiLanguage = user?.localization_lang || navigator.language;
    let supportedLanguages = LANGUAGES.map(v => v.value);

    if (i18n.language !== uiLanguage) {
      let changeValue = supportedLanguages.includes(uiLanguage) ? uiLanguage : "en";
      setLang(changeValue);
    } else {
      setLang(uiLanguage);
    }
  }, [i18n, user?.localization_lang]);

  const handleOpenMenu = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const hdClickOption = (key: string) => {
    if (key !== "language") {
      return handleClose();
    }

    if (moreIconRef.current) {
      setLanguageAnchorEl(moreIconRef.current);
    }
  };

  const hdSelect = (value: string) => setLang(value);

  const allTabs = useTabs({
    tools: {
      onClick: toggleSidebar,
    },
  });
  const textNavTabs = allTabs.filter(tab => !tab.asIcon);
  const iconNavTabs = allTabs.filter(tab => tab.asIcon);

  const shouldTakeTest =
    isUserStudent(user) &&
    user?.features.adaptive_testing &&
    user?.student?.adaptive_test?.status !== AdaptiveTestStatus.completed;
  const name = getUserFullName(user?.profile);
  const isNotificationPanelVisible =
    isUserTeacher(user) || isUserTeamManager(user) || isUserCompanyManager(user);
  const level =
    (user?.features.adaptive_testing &&
      user?.student?.adaptive_test?.evaluated_level?.name) ||
    "";

  return (
    <div className="page-header">
      <Logo />
      <div className="nav-panel-mobile-container">
        {textNavTabs.map(tab => {
          if (!tab.icon || !tab.path) {
            return null;
          }

          return (
            <NavLink
              end
              key={tab.name}
              to={tab.path}
              className={`nav-panel-mobile-icon ${location === tab.path ? "select" : ""}`}
            >
              {tab.icon}
            </NavLink>
          );
        })}
        <div className="nav-panel-mobile-icon" onClick={toggleSidebar}>
          <img src={translateIcon} alt="nav-icon" />
        </div>
      </div>
      <div className="nav-panel-container">
        <NavPanel
          smallWindow={smallWindow}
          tabs={textNavTabs}
          activeColor="#ffffff"
          linkClassName="webapp"
        />
      </div>
      <div className="icon-panel-container">
        <div className="icon-panel-mobile-divider" />
        {(isUserTeacher() || isUserStudent()) && (
          <LanguageSelector className={"language-btn"} onSelect={hdSelect} value={lang} />
        )}

        {isNotificationPanelVisible && <UserNotifications />}

        <div className="icon-panel-divider" />

        <button className="menu-button" onClick={handleOpenMenu}>
          <PersonalIcon />
          <div className="menu-button-container">
            <span className="menu-button-label">{name}</span>
            {shouldTakeTest && (
              <span className="menu-button-text">{t("Common.takeLvTest")}</span>
            )}
            {!shouldTakeTest && level && <span className={"level-name"}>{level}</span>}
          </div>
          <span className={classnames("menu-button-icon", { opened: Boolean(anchorEl) })}>
            <ExpandMoreIcon />
          </span>
        </button>

        <button className="menu-mobile-button" onClick={handleOpenMenu} ref={moreIconRef}>
          <MoreHorizIcon />
        </button>

        <Popover
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          PaperProps={{
            className: "page-header-menu",
          }}
        >
          {iconNavTabs.map(
            ({ icon, title, path, absolutePath, newTab, name: _name }, index) => {
              if (path) {
                return (
                  <Link key={index} to={path} className="menu-item" onClick={handleClose}>
                    <span className="menu-icon">{icon}</span>
                    <span className="menu-label">{t(title)}</span>
                  </Link>
                );
              }
              if (absolutePath) {
                return (
                  <a
                    key={index}
                    href={absolutePath}
                    className="menu-item"
                    onClick={handleClose}
                    {...(newTab && { target: "_blank" })}
                  >
                    <span className="menu-icon">{icon}</span>
                    <span className="menu-label">{t(title)}</span>
                  </a>
                );
              }

              if (_name === "language") {
                return (
                  <button
                    key={index}
                    className="menu-item with-arrow only-mobile"
                    onClick={() => hdClickOption(_name)}
                  >
                    <div>
                      <span className="menu-icon">{icon}</span>
                      <span className="menu-label">{t(title)}</span>
                    </div>

                    <CaretRightIcon />
                  </button>
                );
              }

              return (
                <button key={index} className="menu-item" onClick={handleClose}>
                  <span className="menu-icon">{icon}</span>
                  <span className="menu-label">{t(title)}</span>
                </button>
              );
            }
          )}
          <button className="menu-item" onClick={handleSignOut}>
            <span className="menu-icon">
              <LoginIcon />
            </span>
            <span className="menu-label">{t("Common.Sign Out")}</span>
          </button>
        </Popover>
      </div>

      <LanguageDropdown
        anchorEl={languageAnchorEl}
        transformOrigin={{
          vertical: "top",
          horizontal: 235,
        }}
        onClose={() => setLanguageAnchorEl(null)}
        onSelect={hdSelect}
        value={lang}
      />
    </div>
  );
};

export default PageHeader;
